import React, { useState } from 'react';
import { getFile } from '../../../mutations/ServerStorage'
import { getArchivo } from "../../../helpers/serverStorage";
import { showFile } from '../../../helpers';

/*
  Sección armada a partir de la copia del Catálogo de Preguntas de Encuesta que se guardó al
  generar la encuesta (tblencuesta_secciones.ESTRUCTURA). Las encuestas anteriores al catálogo
  no tienen esa copia y se siguen mostrando con SeccionI, SeccionIV, ...

  Formato de la respuesta (tblencuesta_secciones.RESPUESTA):
  { version: 1, respuestas: { [preguntaCatID]: valor } }
    TEXTO / TEXTO_CORTO  "texto"
    NUMERO               "1,234.5"
    SI_NO                { respuesta: 1 | 0 | null, detalle: "", archivo: "" }
    OPCIONES             { seleccion: [opcionCatID], detalle: "" }
    ARCHIVO              { archivo: "nombre del archivo" }
    TABLA                { filas: { [fila]: { [opcionCatID columna]: valor } }, etiquetas: { [fila]: "texto" } }
                         fila = año (filas por año) | opcionCatID de la fila | opcionCatID_subfila
*/

export const clasificacionAdjunto = (preguntaCatID) => `ADJUNTOSENCUESTA_P${preguntaCatID}`;

const tiposSinRespuesta = ["TITULO", "NOTA"];

export function leerEstructura(estructura) {
    if (!estructura) return null;
    try {
        let resultado = typeof estructura === "string" ? JSON.parse(estructura) : estructura;
        return resultado && Array.isArray(resultado.preguntas) ? resultado : null;
    } catch (error) {
        return null;
    }
}

export function leerRespuesta(respuesta) {
    try {
        let resultado = respuesta ? JSON.parse(respuesta) : null;
        if (resultado && resultado.respuestas) return resultado;
    } catch (error) { }
    return { version: 1, respuestas: {} };
}

export const columnasDe = (pregunta) => pregunta.opciones.filter(o => o.tipoOpcion === "COLUMNA");
export const opcionesDe = (pregunta) => pregunta.opciones.filter(o => o.tipoOpcion === "OPCION");

// Filas de una tabla: { clave, etiqueta, etiquetaEditable, subfila }
export function filasDe(pregunta, ejercicio) {
    let filas = [];
    if (pregunta.modoFilas === "ANIOS") {
        let anio = parseInt(ejercicio);
        for (let i = Number(pregunta.aniosAtras || 0); i >= 1; i--) {
            filas.push({ clave: String(anio - i), etiqueta: `${i} ${i === 1 ? "año" : "años"} atrás ${anio - i}` });
        }
        filas.push({ clave: String(anio), etiqueta: `Est. cierre año actual ${anio}` });
        if (Number(pregunta.incluyeProyeccion) === 1) filas.push({ clave: String(anio + 1), etiqueta: `Proyección ${anio + 1}` });
        return filas;
    }
    let subfilas = pregunta.subfilas ? pregunta.subfilas.split("|").map(s => s.trim()).filter(Boolean) : [];
    pregunta.opciones.filter(o => o.tipoOpcion === "FILA").forEach(fila => {
        let base = { claveEtiqueta: String(fila.opcionCatID), etiqueta: fila.etiqueta, etiquetaEditable: Number(fila.etiquetaEditable) === 1 };
        if (subfilas.length === 0) filas.push({ ...base, clave: String(fila.opcionCatID) });
        else subfilas.forEach((subfila, i) => filas.push({ ...base, clave: `${fila.opcionCatID}_${i}`, subfila, primeraSubfila: i === 0, numSubfilas: subfilas.length }));
    });
    return filas;
}

export function numero(valor) {
    let resultado = parseFloat(String(valor === undefined || valor === null ? "" : valor).replace(/,/g, ""));
    return isNaN(resultado) ? 0 : resultado;
}

export function formatoNumerico(valor) {
    valor = String(valor).replace(/,/g, "").replace(/[^0-9.-]/g, "");
    if (valor === "" || valor === "-") return valor;
    let partes = valor.split(".");
    partes[0] = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return partes.length > 1 ? `${partes[0]}.${partes.slice(1).join("")}` : partes[0];
}

export function sumaFila(pregunta, valoresFila = {}) {
    let total = columnasDe(pregunta).filter(c => c.tipoDato === "NUMERO").reduce((suma, c) => suma + numero(valoresFila[c.opcionCatID]), 0);
    return formatoNumerico(String(Math.round(total * 100) / 100));
}

function valorInicial(pregunta) {
    switch (pregunta.tipo) {
        case "TEXTO": case "TEXTO_CORTO": case "NUMERO": return pregunta.valorDefault || "";
        case "SI_NO": return { respuesta: null, detalle: "", archivo: "" };
        case "OPCIONES": return { seleccion: [], detalle: "" };
        case "ARCHIVO": return { archivo: "" };
        case "TABLA": return { filas: {}, etiquetas: {} };
        default: return null;
    }
}

function detalleVisible(pregunta, valor) {
    if (!pregunta.etiquetaDetalle || !valor) return false;
    if (pregunta.tipo === "SI_NO") return valor.respuesta === 1;
    if (pregunta.tipo === "OPCIONES") return opcionesDe(pregunta).some(o => Number(o.muestraDetalle) === 1 && valor.seleccion.includes(o.opcionCatID));
    return false;
}

// Preguntas obligatorias sin contestar (para validar antes de guardar)
export function preguntasSinContestar(estructura, respuesta) {
    let datos = leerEstructura(estructura);
    if (!datos) return [];
    let { respuestas } = leerRespuesta(respuesta);
    return datos.preguntas.filter(p => Number(p.obligatoria) === 1 && !tiposSinRespuesta.includes(p.tipo)).filter(p => {
        let valor = respuestas[p.preguntaCatID];
        if (valor === undefined || valor === null) return true;
        switch (p.tipo) {
            case "SI_NO": return valor.respuesta !== 1 && valor.respuesta !== 0;
            case "OPCIONES": return !valor.seleccion || valor.seleccion.length === 0;
            case "ARCHIVO": return !valor.archivo;
            case "TABLA": return Object.values(valor.filas || {}).every(f => Object.values(f).every(v => String(v).trim() === ""));
            default: return String(valor).trim() === "";
        }
    }).map(p => p.texto || "Tabla");
}

function SeccionDinamica({ estructura, respuesta, setRespuesta, setAdjunto, seccionID, ejercicio, disabled }) {

    const datos = leerEstructura(estructura);
    const [stRespuestas, setRespuestas] = useState(() => leerRespuesta(respuesta));
    const [stAdjuntos, setAdjuntos] = useState({});

    if (!datos) return <></>;

    const valorDe = (pregunta) => {
        let valor = stRespuestas.respuestas[pregunta.preguntaCatID];
        return valor === undefined ? valorInicial(pregunta) : valor;
    };

    function guardarValor(preguntaCatID, valor) {
        let nuevas = { ...stRespuestas, respuestas: { ...stRespuestas.respuestas, [preguntaCatID]: valor } };
        setRespuestas(nuevas);
        setRespuesta(JSON.stringify(nuevas));
    }

    function seleccionarArchivo(pregunta, event, actualizarValor) {
        let [archivo] = Array.from(event.target.files || []);
        if (!archivo) return;
        let adjuntos = { ...stAdjuntos, [pregunta.preguntaCatID]: archivo };
        setAdjuntos(adjuntos);
        setAdjunto(adjuntos);
        actualizarValor(archivo.name);
    }

    async function descargar(pregunta) {
        let archivo = await getFile({ origenID: seccionID, tipo: 'ADJUNTO', clasificacion: clasificacionAdjunto(pregunta.preguntaCatID) });
        if (!archivo) return;
        let documento = await getArchivo(archivo.ruta);
        let b64 = documento.substring(documento.indexOf(',') + 1);
        let ext = documento.substring(documento.indexOf(':') + 1, documento.indexOf(';'));
        showFile(b64, archivo.nombreArchivo, ext);
    }

    const textoArea = (valor, onChange) => (
        <div className="RespuestasInput">
            <div className="InputText">
                <div className="txt">
                    <textarea value={valor || ""} onChange={event => onChange(event.target.value)} disabled={disabled} />
                </div>
            </div>
        </div>
    );

    const radio = (marcado, etiqueta, onClick, key) => (
        <div key={key} className="RadioButton" onClick={() => disabled === true ? "" : onClick()}>
            <div className="rbt">
                <input disabled={disabled} type="radio" checked={marcado} readOnly />
            </div>
            <div className={"Etiqueta " + (marcado ? " cSelec" : "")}>{etiqueta}</div>
        </div>
    );

    const campoArchivo = (pregunta, nombreArchivo, actualizarValor) => {
        let pendiente = stAdjuntos[pregunta.preguntaCatID];
        return (
            <div className="RespuestasInput">
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                    {nombreArchivo && !pendiente && <input type="button" value={`Descargar adjunto (${nombreArchivo})`} onClick={() => descargar(pregunta)} />}
                    {pendiente && <span>{`Se adjuntará: ${pendiente.name}`}</span>}
                    {!disabled && <input type="file" accept=".doc, .docx, .xlsm, .xls, .xlsx, .xml, .pptx, .pdf, .jpg, .png, .jpeg" onChange={event => seleccionarArchivo(pregunta, event, actualizarValor)} />}
                </div>
            </div>
        );
    };

    function renderTabla(pregunta) {
        let valor = valorDe(pregunta);
        let columnas = columnasDe(pregunta);
        let filas = filasDe(pregunta, ejercicio);
        let conSubfilas = filas.some(f => f.subfila);
        let anchoEtiqueta = "150px";
        let anchoColumna = "110px";

        function cambiarCelda(fila, columna, texto) {
            let valorFila = { ...(valor.filas[fila.clave] || {}) };
            valorFila[columna.opcionCatID] = columna.tipoDato === "NUMERO" ? formatoNumerico(texto) : texto;
            let sumas = columnas.filter(c => c.tipoDato === "SUMA");
            sumas.forEach(c => { valorFila[c.opcionCatID] = sumaFila(pregunta, valorFila); });
            guardarValor(pregunta.preguntaCatID, { ...valor, filas: { ...valor.filas, [fila.clave]: valorFila } });
        }

        function cambiarEtiqueta(fila, texto) {
            guardarValor(pregunta.preguntaCatID, { ...valor, etiquetas: { ...(valor.etiquetas || {}), [fila.claveEtiqueta]: texto } });
        }

        const celda = (fila, columna) => {
            let valorFila = valor.filas[fila.clave] || {};
            if (columna.tipoDato === "SUMA") {
                return <input className="cInputNumerico" value={sumaFila(pregunta, valorFila)} disabled />;
            }
            let valorCelda = valorFila[columna.opcionCatID] === undefined ? "" : valorFila[columna.opcionCatID];
            if (columna.tipoDato === "TEXTO_LARGO") {
                return <textarea value={valorCelda} onChange={event => cambiarCelda(fila, columna, event.target.value)} disabled={disabled} />;
            }
            return <input className={columna.tipoDato === "NUMERO" ? "cInputNumerico" : "cInputText"} value={valorCelda}
                onChange={event => cambiarCelda(fila, columna, event.target.value)} disabled={disabled} />;
        };

        return (
            <div className="cSeccionTabla" style={{ width: "100%", paddingLeft: "8px", paddingRight: "8px", overflow: "hidden" }}>
                <div className="cContenedorTabla">
                    <div className="cTabla">
                        <div className="cRowEncabezado">
                            <div className="cCelda" style={{ width: anchoEtiqueta }}>{pregunta.etiquetaDetalle || ""}</div>
                            {conSubfilas && <div className="cCelda" style={{ width: anchoColumna }}></div>}
                            {columnas.map(c => <div key={c.opcionCatID} className="cCelda" style={{ width: anchoColumna }}>{c.etiqueta}</div>)}
                        </div>
                        {filas.map(fila => (
                            <div key={fila.clave} className="cRow">
                                <div className="cCelda" style={{ width: anchoEtiqueta }}>
                                    {(!fila.subfila || fila.primeraSubfila) && (fila.etiquetaEditable
                                        ? <>{fila.etiqueta}<input className="cInputText" value={(valor.etiquetas || {})[fila.claveEtiqueta] || ""}
                                            onChange={event => cambiarEtiqueta(fila, event.target.value)} disabled={disabled} /></>
                                        : fila.etiqueta)}
                                </div>
                                {conSubfilas && <div className="cCelda" style={{ width: anchoColumna }}>{fila.subfila}</div>}
                                {columnas.map(c => <div key={c.opcionCatID} className="cCelda" style={{ width: anchoColumna }}>{celda(fila, c)}</div>)}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    function renderPregunta(pregunta) {
        let valor = valorDe(pregunta);
        let id = pregunta.preguntaCatID;

        switch (pregunta.tipo) {
            case "TITULO":
                return <div className="Pregunta">{pregunta.texto}</div>;
            case "NOTA":
                return <div>{pregunta.texto}</div>;
            case "TEXTO":
                return <><div className="Pregunta">{pregunta.texto}</div>{textoArea(valor, texto => guardarValor(id, texto))}</>;
            case "TEXTO_CORTO":
            case "NUMERO":
                return <>
                    <div className="Pregunta">{pregunta.texto}</div>
                    <div className="RespuestasInput">
                        <input className={pregunta.tipo === "NUMERO" ? "cInputImporte" : "cInputText"} value={valor || ""} disabled={disabled}
                            onChange={event => guardarValor(id, pregunta.tipo === "NUMERO" ? formatoNumerico(event.target.value) : event.target.value)} />
                    </div>
                </>;
            case "SI_NO": {
                let elegir = (respuestaSiNo) => guardarValor(id, { ...valor, respuesta: respuestaSiNo, ...(respuestaSiNo === 0 ? { detalle: "" } : {}) });
                return <>
                    <div className="Pregunta">{pregunta.texto}</div>
                    {radio(valor.respuesta === 1, "SI", () => elegir(1), "si")}
                    {radio(valor.respuesta === 0, "NO", () => elegir(0), "no")}
                    {detalleVisible(pregunta, valor) && <>
                        <div className="Pregunta">{pregunta.etiquetaDetalle}</div>
                        {pregunta.tipoDetalle === "ARCHIVO"
                            ? campoArchivo(pregunta, valor.archivo, nombre => guardarValor(id, { ...valor, archivo: nombre }))
                            : textoArea(valor.detalle, texto => guardarValor(id, { ...valor, detalle: texto }))}
                    </>}
                </>;
            }
            case "OPCIONES": {
                let alternar = (opcionCatID) => {
                    let seleccion = valor.seleccion.includes(opcionCatID) ? valor.seleccion.filter(s => s !== opcionCatID) : [...valor.seleccion, opcionCatID];
                    let nuevo = { ...valor, seleccion };
                    if (!detalleVisible(pregunta, nuevo)) nuevo.detalle = "";
                    guardarValor(id, nuevo);
                };
                return <>
                    <div className="Pregunta">{pregunta.texto}</div>
                    {opcionesDe(pregunta).map(o => radio(valor.seleccion.includes(o.opcionCatID), o.etiqueta, () => alternar(o.opcionCatID), o.opcionCatID))}
                    {detalleVisible(pregunta, valor) && <>
                        <div className="Pregunta">{pregunta.etiquetaDetalle}</div>
                        {textoArea(valor.detalle, texto => guardarValor(id, { ...valor, detalle: texto }))}
                    </>}
                </>;
            }
            case "ARCHIVO":
                return <><div className="Pregunta">{pregunta.texto}</div>{campoArchivo(pregunta, valor.archivo, nombre => guardarValor(id, { archivo: nombre }))}</>;
            case "TABLA":
                return <>{pregunta.texto && <div className="Pregunta">{pregunta.texto}</div>}{renderTabla(pregunta)}</>;
            default:
                return <></>;
        }
    }

    return (<>
        <div className="cContenedorSeccion">
            {datos.preguntas.map(pregunta => (
                <div key={pregunta.preguntaCatID} className="ElementoPregunta">
                    {renderPregunta(pregunta)}
                    {Number(pregunta.obligatoria) === 1 && !disabled && <span style={{ color: "#c62828", fontSize: "12px" }}>* Obligatoria</span>}
                </div>
            ))}
        </div>
    </>);
}

export default SeccionDinamica;
